import React, { type FC, type ReactNode } from 'react'
import { Box } from './Box'
import type { BoxDto } from '../types/generated'
import { BoxesLayout } from '../layout/BoxesLayout'
import { ScrollLoader } from '../system/ScrollLoader'
import { styled } from '@linaria/react'
import { EmptyList } from './EmptyList'
import type { InfiniteData } from '@tanstack/react-query'
import { useTrackBoxListViewed } from '../hooks/useTrackBoxListViewed'

export const ScrollBoxes: FC<{
  data: InfiniteData<{ data: BoxDto[] }> | undefined
  fetchNextPage: () => void
  isFetching: boolean
  isFetchingNextPage: boolean
  isError: boolean
  refetch: () => void
  titleElement: ReactNode | null
  tabsElement: ReactNode
  emptyNavigateTo?: string
  emptyButtonContent?: ReactNode
  filterType?: 'all' | 'my' | 'my-votes' | 'finished'
}> = ({
  data,
  fetchNextPage,
  isFetching,
  isFetchingNextPage,
  isError,
  refetch,
  titleElement,
  tabsElement,
  emptyNavigateTo,
  emptyButtonContent,
  filterType,
}) => {
  useTrackBoxListViewed(data, isFetching, filterType)

  const isEmpty = data?.pages.every(page => page.data.length === 0)

  return (
    <BoxesLayout
      loading={isFetching && !isFetchingNextPage && !data}
      error={isError}
      onRetryFetch={refetch}
      titleElement={titleElement}
      tabsElement={tabsElement}
    >
      <ScrollLoader loading={isFetching} onShouldLoad={fetchNextPage}>
        <ScrollBoxesContainer>
          {isEmpty ? (
            <FullWidthCell key="empty-state">
              <EmptyList
                navigateTo={emptyNavigateTo}
                buttonContent={emptyButtonContent}
              />
            </FullWidthCell>
          ) : (
            Array.from(
              new Map(
                (data?.pages ?? [])
                  .flatMap(page => page.data)
                  .map(box => [box.address, box]),
              ).values(),
            ).map(box => <Box key={box.address} box={box} />)
          )}
        </ScrollBoxesContainer>
      </ScrollLoader>
    </BoxesLayout>
  )
}

const ScrollBoxesContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 360px), 1fr));
  gap: 12px;
  min-height: 330px;
`

const FullWidthCell = styled.div`
  grid-column: 1 / -1;
`
